import _ from 'lodash';
import { getQtyFromRatio } from 'spw-components/worksheet/utils';
import {
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MAX_QTY,
  MAX_SEAT_CAP,
  MAX_USF,
  SHARED_PRIVATE_OFFICE,
} from 'spw-constants';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import WorksheetRow from '../models/WorksheetRow';

export const onBlurRatio = ({
  row,
  currentRatio,
  editValue,
  totalWorkSeats,
  isRatio,
  isDirty,
}: {
  row: Partial<WorksheetRow>;
  currentRatio: number;
  editValue: string;
  totalWorkSeats: number;
  isRatio?: boolean;
  isDirty: boolean;
}) => {
  if (
    Math.round(currentRatio) === Math.round(parseFloat(editValue)) &&
    !isDirty
  ) {
    return {
      newQuantity: row.quantity,
      newRatio: parseFloat(editValue),
      rowSeatCap: row.seatCap,
    };
  }

  let { seatCap: rowSeatCap } = row;
  if (!rowSeatCap && isIWCategoryOrSubcategory(row.projectSpaceTypeCategory))
    rowSeatCap =
      row?.spaceType?.parent?.spaceType === SHARED_PRIVATE_OFFICE ? 2 : 1;

  const newRatio = _.isNaN(parseFloat(editValue))
    ? 0
    : _.min([parseFloat(editValue), isRatio ? totalWorkSeats : 100]);
  let newQuantity: number;

  if (isRatio) {
    newQuantity = getQtyFromRatio(newRatio, totalWorkSeats);
  } else {
    const workSeats = (totalWorkSeats * newRatio) / 100;
    newQuantity = workSeats / rowSeatCap;

    if (!_.isFinite(newQuantity)) {
      newQuantity = 0;
    }
  }

  if (!_.isFinite(newQuantity)) {
    newQuantity = 0;
  }
  return { newQuantity, newRatio, rowSeatCap };
};

export const onBlurQuantity = ({
  row,
  editValue,
  isDirty,
}: {
  row: WorksheetRow;
  editValue: string;
  isDirty: boolean;
}) => {
  if (row.quantity === parseFloat(editValue) && !isDirty) {
    return {
      newQuantity: row.quantity,
      rowSeatCap: row.seatCap,
    };
  }
  const { seatCap: rowSeatCap } = row;

  const newQuantity = _.isNaN(parseFloat(editValue))
    ? 0
    : _.min([parseFloat(editValue), MAX_QTY]);

  return {
    rowSeatCap,
    newQuantity,
  };
};

export const onBlurSeatCap = ({
  row,
  editValue,
  isDirty,
}: {
  row: WorksheetRow;
  editValue: string;
  isDirty: boolean;
}) => {
  if (row.seatCap === parseFloat(editValue) && !isDirty) {
    return {
      newSeatCap: row.seatCap,
    };
  }
  let newSeatCap = _.isNaN(parseFloat(editValue))
    ? 0
    : _.min([parseFloat(editValue), MAX_SEAT_CAP]);
  if (!newSeatCap || newSeatCap < 0) {
    newSeatCap = 0;
  }
  const shouldDefaultToMinValue =
    row.category === INDIVIDUAL_WORKSPACES_CATEGORY && newSeatCap === 0;
  if (shouldDefaultToMinValue) {
    newSeatCap = 1;
  }

  return {
    newSeatCap,
  };
};

export const onBlurUsf = (
  row: WorksheetRow,
  editValue: string,
  isDirty: boolean
) => {
  if (editValue === '') {
    return { ...row, usf: 0 };
  }
  const { sizeX, sizeY } = row;

  let newUsf = _.isNaN(parseFloat(editValue))
    ? 0
    : _.min([parseFloat(editValue), MAX_USF]);
  const newRounded = Math.round(newUsf * 100);
  const oldRounded = Math.round(row.usf) * 100;
  if (newRounded === oldRounded && !isDirty) {
    newUsf = row.usf;
  }

  if (newUsf < 0) {
    newUsf = 0;
  }
  // keep the current dimension values if the new usf is exactly equal to the product of the x and y dims
  const keepDims = newUsf === sizeX * sizeY;
  return {
    ...row,
    usf: newUsf,
    sizeX: keepDims ? sizeX : 0,
    sizeY: keepDims ? sizeY : 0,
  };
};
